import { DatePipe, NgIf, NgOptimizedImage } from '@angular/common';
import { Component, HostListener, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { RestrictedNavigationService } from '@services/restricted-navigation.service';

@Component({
  selector: 'app-testosterone-hq-layout',
  templateUrl: './testosterone-hq-layout.component.html',
  styleUrls: ['./testosterone-hq-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [NgIf, DatePipe, NgOptimizedImage],
})
export class TestosteroneHqLayoutComponent implements OnInit, OnDestroy {
  public restrictedNavigation: boolean = false;
  public today: Date = new Date();
  public isSticked: boolean = false;
  public isShowed: boolean = false;

  private lastScrollTop: number = 0;
  private restrictedNavigationService = inject(RestrictedNavigationService);
  private wisepopsBodyObserver: MutationObserver | null = null;
  private wisepopsResizeObserver: ResizeObserver | null = null;
  private wisepopsAttributeObserver: MutationObserver | null = null;
  private wisepopsTransitionEndHandler: (() => void) | null = null;
  private readonly WISEPOPS_HEIGHT_VAR = '--wisepops-banner-height';

  @HostListener('window:scroll', [])
  public onWindowScroll(): void {
    const scrollTop = window.scrollY;

    if (scrollTop > 1) {
      this.isSticked = true;
    } else {
      this.isSticked = false;
      this.isShowed = false;
    }

    if (this.isSticked) {
      this.isShowed = scrollTop < this.lastScrollTop;
    }

    this.lastScrollTop = scrollTop;
    this.measureWisepopsHeight();
  }

  public ngOnInit(): void {
    this.restrictedNavigationService.restrictedNavigationObserver.subscribe((r) => (this.restrictedNavigation = r));
    this.setupWisepopsObserver();
  }

  public ngOnDestroy(): void {
    this.wisepopsBodyObserver?.disconnect();
    this.wisepopsResizeObserver?.disconnect();
    this.wisepopsAttributeObserver?.disconnect();
    const wisepopsRoot = document.querySelector<HTMLElement>('.wisepops-root');
    if (wisepopsRoot && this.wisepopsTransitionEndHandler) {
      wisepopsRoot.removeEventListener('transitionend', this.wisepopsTransitionEndHandler, true);
      wisepopsRoot.removeEventListener('animationend', this.wisepopsTransitionEndHandler, true);
    }
    document.documentElement.style.removeProperty(this.WISEPOPS_HEIGHT_VAR);
  }

  private setupWisepopsObserver(): void {
    this.tryAttachWisepopsResizeObserver();

    this.wisepopsBodyObserver = new MutationObserver(() => {
      this.tryAttachWisepopsResizeObserver();
    });

    this.wisepopsBodyObserver.observe(document.body, { childList: true });
  }

  private measureWisepopsHeight(): void {
    const wisepopsRoot = document.querySelector<HTMLElement>('.wisepops-root');
    const height = wisepopsRoot ? Math.max(0, wisepopsRoot.getBoundingClientRect().bottom) : 0;
    document.documentElement.style.setProperty(this.WISEPOPS_HEIGHT_VAR, `${height}px`);
  }

  private tryAttachWisepopsResizeObserver(): void {
    const wisepopsRoot = document.querySelector<HTMLElement>('.wisepops-root');
    if (!wisepopsRoot || this.wisepopsResizeObserver) return;

    const updateHeight = (): void => this.measureWisepopsHeight();

    this.wisepopsResizeObserver = new ResizeObserver(updateHeight);
    this.wisepopsResizeObserver.observe(wisepopsRoot);

    // Watch style/class attribute changes for transform-based hide animations
    this.wisepopsAttributeObserver = new MutationObserver(updateHeight);
    this.wisepopsAttributeObserver.observe(wisepopsRoot, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      subtree: true,
    });

    // Re-measure after CSS transitions/animations finish
    this.wisepopsTransitionEndHandler = updateHeight;
    wisepopsRoot.addEventListener('transitionend', this.wisepopsTransitionEndHandler, true);
    wisepopsRoot.addEventListener('animationend', this.wisepopsTransitionEndHandler, true);
  }
}
